import { useQuery } from '@tanstack/react-query';
import { searchQuery, useAxiosCmsInstance } from '@gov-nx/api';
import type { SignpostRequestParams } from '@gov-nx/api';
import { useExtraConfigContext } from '@gov-nx/core/app';
import { useAppLanguage } from '../Localization/useAppLanguage';

type SearchSignpostProps = Pick<
	SignpostRequestParams,
	'externalServiceLink'
> & {
	searchEnabled: boolean;
};

export const useSearchSignpost = ({
	externalServiceLink,
	searchEnabled,
}: SearchSignpostProps) => {
	const {
		extraConfig: { environment, application },
	} = useExtraConfigContext();
	const { language } = useAppLanguage();
	const { axiosCmsBaseInstance } = useAxiosCmsInstance();

	const query = useQuery({
		queryKey: ['cms-search', externalServiceLink],
		queryFn: async () => {
			if (!application || !environment) {
				return;
			}

			const response = await searchQuery(axiosCmsBaseInstance, {
				env: environment,
				app: application,
				locale: language,
				externalServiceLink,
			});

			return response.data;
		},
		enabled: !!externalServiceLink && searchEnabled,
	});

	return {
		searchedSignpost: externalServiceLink
			? query.data?.find((item) =>
					item.externalService?.links?.some((link) =>
						link.link?.includes(externalServiceLink)
					)
				)
			: undefined,
		isFetching: query.isFetching,
		isError: query.isError,
	};
};
